import * as Sentry from "@sentry/browser";
import Cookies from "js-cookie";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useIdle } from "react-use";
import DataTypes from "~data";
import {
  TeamAuthStatusData,
  TeamMembershipAuthStatusData,
  UserAuthStatusData,
} from "~services/app/endpoints/auth/statusShow/response";

import api from "../api-client";
import { confirmUnexpectedError, makeApiErrorEvent } from "../api-errors";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { setLoggerContext } from "../log";
import { path } from "../paths";
import { useCachedState } from "../use/cached-object";
import useExtensionAuthUpdate from "../use/extension-auth-update";
import { presence } from "../utils";
import { useGoogleTagManager } from "../vendor/use-google-tag-manager";
import useIntercom from "../vendor/use-intercom";
import { useModal } from "./ModalProvider";

type AdminRole = Exclude<DataTypes["Users"]["adminRoles"], null>[number];

type ContextType = {
  adminRoles: AdminRole[];
  logout: () => Promise<void>;
  promptForMfa: boolean;
  promptForPassword: boolean;
  refresh: () => Promise<void>;
  stopImpersonation: () => Promise<void>;
} & (
  | {
      impersonating: boolean;
      teamMemberships: Array<TeamMembershipAuthStatusData & { team: TeamAuthStatusData }>;
      teams: Array<TeamAuthStatusData>;
      user: UserAuthStatusData;
    }
  | {
      impersonating: null;
      teamMemberships: null;
      teams: null;
      user: null;
    }
);

type ProviderProps = {
  children: JSX.Element;
};

const AuthenticationContext = createContext<ContextType>({
  adminRoles: [],
  impersonating: null,
  logout: async () => {},
  promptForMfa: false,
  promptForPassword: false,
  refresh: async () => {},
  stopImpersonation: async () => {},
  teamMemberships: null,
  teams: null,
  user: null,
});

export default function AuthenticationProvider({ children }: ProviderProps) {
  const [fetched, setFetched] = useState(false);
  const fetchedRef = useRef(false);
  const { confirm } = useModal();
  const [promptForPassword, setPromptForPassword] = useState(false);
  const [promptForMfa, setPromptForMfa] = useState(false);
  const [user, setUser] = useCachedState<null | UserAuthStatusData>(null);
  const [teams, setTeams] = useCachedState<Array<TeamAuthStatusData> | null>(null);
  const [impersonating, setImpersonating] = useState<boolean | null>(null);
  const [teamMemberships, setTeamMemberships] = useCachedState<null | TeamMembershipAuthStatusData[]>(null);
  const [adminRoles, setAdminRoles] = useState<AdminRole[]>([]);
  const isIdle = useIdle(60_000);
  useIntercom({
    email: user?.email,
    enable: fetched && !impersonating && !user?.isAdmin,
    hashedEmail: presence(user?.intercomHashedEmail),
    name: user?.name ?? undefined,
  });
  useGoogleTagManager({
    enable: fetched && !impersonating && !user?.isAdmin,
    userId: user?.id,
  });
  useExtensionAuthUpdate();

  const refresh = useCallback(async () => {
    const { auth } = await api("auth.status.show");
    fetchedRef.current = true;
    setFetched(true);
    if (auth) {
      const originalCookie = Cookies.get("upscope_locale");
      if (typeof auth.language === "string" && originalCookie !== auth.language) {
        Cookies.set("upscope_locale", auth.language, { expires: 365 });
        if (Cookies.get("upscope_locale") === auth.language)
          // Will not get saved if cookies are disabled in embedded version
          window.reload();
      }
      setUser(auth.user);
      setTeams(auth.teams);
      setImpersonating(auth.impersonating);
      setTeamMemberships(auth.teamMemberships);
      setPromptForPassword(auth.promptForPassword);
      setPromptForMfa(auth.promptForMfa);
      setAdminRoles(auth.adminRoles);
    } else {
      setUser(null);
      setTeams(null);
      setTeamMemberships(null);
      setImpersonating(false);
      setAdminRoles([]);
    }
  }, [setTeamMemberships, setTeams, setUser]);

  const teamMembershipsWithTeam: Array<TeamMembershipAuthStatusData & { team: TeamAuthStatusData }> | null =
    useMemo(() => {
      return (
        teamMemberships?.map((membership: TeamMembershipAuthStatusData) => {
          const m = { ...membership } as TeamMembershipAuthStatusData & { team: TeamAuthStatusData };
          m.team = teams!.find((t) => t.id === membership.teamId)!;
          return m;
        }) || null
      );
    }, [teams, teamMemberships]);

  const logout = useCallback(async () => {
    await api("auth.sessions.delete");
    setUser(null);
    setTeams(null);
    setTeamMemberships(null);
    location.href = path("up.root", {}); // Cannot  use navigate because this happens outside of the router
  }, [setTeamMemberships, setTeams, setUser]);

  const stopImpersonation = useCallback(async () => {
    const { redirectUrl } = await api("global.impersonation.delete");

    location.href = redirectUrl;
  }, []);

  useEffect(() => {
    if (isIdle) return;

    refresh().catch((error: unknown) => {
      if (fetchedRef.current) throw error;
      Sentry.captureException(new Error("Initial auth check failed for auth.status.show", { cause: error }));
      document.dispatchEvent(makeApiErrorEvent("unexpected"));
    });
  }, [refresh, isIdle]);

  useEffect(() => {
    if (fetched) return;

    const listener = (event: Event) => {
      if (event instanceof CustomEvent && event.detail?.reason === "unexpected") confirmUnexpectedError(confirm);
    };
    document.addEventListener("apierror", listener);

    return () => {
      document.removeEventListener("apierror", listener);
    };
  }, [confirm, fetched]);

  useEffect(() => {
    setLoggerContext({
      admin: user?.isAdmin,
      user: user?.id ?? "unauthenticated",
    });
  }, [user]);

  if (!fetched) return <LoadingSpinner />;

  return (
    <AuthenticationContext.Provider
      value={
        {
          adminRoles,
          impersonating,
          logout,
          promptForMfa,
          promptForPassword,
          refresh,
          stopImpersonation,
          teamMemberships: teamMembershipsWithTeam,
          teams,
          user,
        } as ContextType
      }
    >
      {children}
    </AuthenticationContext.Provider>
  );
}

export function useAuthentication() {
  return useContext(AuthenticationContext);
}
